<script setup lang="ts">
import type { CondoTalkToAgentButtonProps } from '.';
import type { Condo } from '~/core/entities/condo/Condo';
import type { FormCondoClicked } from '~/types/mixpanel';

import { useEvents } from '~/composables/events/useEvents';
import { useTypeformPopup } from '~/composables/useTypeform';
import { MixpanelProps } from '~/core/service/mixpanel';
import { CONDO_DETAILS } from '~~/utils/symbols';

import { ContactFormModal } from '~/components/ContactFormModal';

defineComponent({
	inheritAttrs: false
});

const condominium = inject(CONDO_DETAILS);

const condo = computed<Condo | null>(() => condominium?.value?.condo || null);

const props = withDefaults(defineProps<CondoTalkToAgentButtonProps>(), {
	size: 'md'
});

const { t } = useI18n({
	useScope: 'local',
	messages: {
		pt: {
			CTA: 'Falar com especialista'
		}
	}
});

const { condoTalkToAgentTypeformId } = useRuntimeConfig().public;
const { openTypeform } = useTypeformPopup(
	condoTalkToAgentTypeformId as string,
	{
		tracking: {
			condo_id: condo.value?.id ?? ''
		}
	}
);

const { loggedIn } = useUserSession();
const { trackFormCondoClicked } = useEvents();

const companyDistribution = computed<string>(
	() =>
		props.companyDistribution || condominium?.value?.companyDistribution || ''
);

const userStatus = computed<FormCondoClicked['user_status']>(() =>
	loggedIn.value ? 'logged in' : 'logged out'
);

const eventData = computed<FormCondoClicked>(() => ({
	[MixpanelProps.CONDO_NAME]: condo.value?.name ?? '',
	[MixpanelProps.CTA_TEXT]: t('CTA'),
	[MixpanelProps.USER_STATUS]: userStatus.value,
	[MixpanelProps.CURRENT_PAGE]: 'Detalhe do Condomínio',
	[MixpanelProps.SECTION_NAME]: props.sectionName
}));

const isContactFormModalOpen = shallowRef(false);

function handleClick() {
	trackFormCondoClicked(eventData.value);
	isContactFormModalOpen.value = true;
}

const buttonProps = computed(() => ({
	class: props.buttonClasses,
	variant: props.variant,
	size: props.size
}));
</script>

<template>
	<ContactFormModal
		v-model:model-value="isContactFormModalOpen"
		:condo-id="condo?.id"
		:condo-name="condo?.name"
		:company-distribution="companyDistribution"
		context="condominium"
	/>

	<Button
		data-test-id="details-contact-button"
		v-bind="buttonProps"
		@click="handleClick"
	>
		{{ t('CTA') }}</Button
	>
</template>
